import { useRouter } from "next/router";
import className from "classnames/bind";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import Results from "./Results/Results";
import { easeOutQuad } from "@/core/easing";
import NoResults from "./Results/NoResults";
import CloseIcon from "@/assets/svg/close.svg";
import { updateBodyScroll } from "@/core/utils";
import SearchIcon from "@/assets/svg/search.svg";
import { useScroll } from "@/providers/ScrollContext";
import { useSearch } from "@/providers/SearchContext";
import { Container, MaterialLoader } from "@components";
import { AcfMediaFragment } from "@/__generated__/graphql";

import styles from "./SearchTray.module.scss";
import { useViewSize } from "@/providers/ViewSizeContext";
const cx = className.bind(styles);

type SearchTrayProps = SiteNavigation["navigationSettingsFields"];

export default function SearchTray(props: SearchTrayProps) {
  const {
    showSearchTray,
    autoCompleteLoading,
    resetSearch,
    hasSearched,
    setHasSearched,
    isSearchPage,
    updateResults,
    autoCompleteResults,
    setIgnoreSearch,
    lastSearchTerm,
    fetchAutoCompleteResults,
    setShowSearchTray,
    goingToSearch,
    searchPath,
    shopResults,
  } = useSearch();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resultsWrapperRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const inputEl = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { isMobile } = useViewSize();
  const { searchPlaceholders, searchAriaLabel, clearAriaLabel, noResultsLabel, resultsCta } = props;
  const resultsReady = useMemo(() => Boolean(lastSearchTerm) && hasSearched, [lastSearchTerm, hasSearched]);
  const showNoResults = useMemo(() => resultsReady && autoCompleteResults.length === 0 && shopResults.length === 0, [resultsReady, autoCompleteResults, shopResults]);
  const showResultsList = useMemo(() => resultsReady && (autoCompleteResults.length > 0 || shopResults.length > 0), [resultsReady, autoCompleteResults, shopResults]);
  const showLoader = useMemo(() => !showResultsList && !showNoResults && autoCompleteLoading, [showResultsList, autoCompleteLoading, showNoResults]);
  const lastPlaceholderIndex = useRef<number>(-1);
  const searchPlaceholder = useMemo(() => {
    if (!showSearchTray) return;
    if (searchPlaceholders && searchPlaceholders.length > 0) {
      let randomIndex: number;
      do {
        randomIndex = Math.floor(Math.random() * searchPlaceholders.length);
      } while (randomIndex === lastPlaceholderIndex.current && searchPlaceholders.length > 1);
      lastPlaceholderIndex.current = randomIndex;
      return searchPlaceholders[randomIndex]?.placeholder || "Search";
    }
    return "Search";
  }, [searchPlaceholders, showSearchTray]);
  const { scrollY, lastScrollY, setLastScrollY } = useScroll();
  const [isFocused, setIsFocused] = useState(false);

  const onClose = () => {
    if (!isSearchPage && !goingToSearch) {
      resetSearch();
      setSearchTerm("");
    } else if (isSearchPage && searchTerm !== lastSearchTerm) {
      setIgnoreSearch(true);
      router.push(`${searchPath}?s=${encodeURIComponent(searchTerm)}`, undefined, { shallow: true });
      updateResults();
    }
  };

  const handleClearInput = () => {
    setSearchTerm("");
    resetSearch();
    inputEl.current?.focus();
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const value = e.target.value;
    setSearchTerm(value);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // Sole debounce for autocomplete; the context fires the fetch immediately
    timeoutRef.current = setTimeout(() => {
      if (value && value.length >= 3) {
        fetchAutoCompleteResults(value);
      } else if(!value || value.length === 0) {
        resetSearch();
      } else {
        // 1-2 characters: too short to search, so hide the previous term's
        // results instead of leaving them rendered against a shorter input
        setHasSearched(false);
      }
    }, 300);
  };

  const checkShouldClose = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setShowSearchTray(false);
    }
  }

  const closeTray = () => setShowSearchTray(false);

  useEffect(() => {
    if (!isMobile) updateBodyScroll(showSearchTray && showResultsList);
  }, [showResultsList, showSearchTray]);

  useEffect(() => {
    router.events.on("routeChangeStart", closeTray);
    return () => {
      router.events.off("routeChangeStart", closeTray);
    };
  }, []);

  useEffect(() => {
    if (isMobile) updateBodyScroll(showSearchTray);
    if (showSearchTray) {
      if (!isMobile && !showResultsList) {
        if (Math.abs(scrollY - lastScrollY) >= 30) {
          closeTray();
        }
      }
    }
    setLastScrollY(scrollY);
  }, [scrollY, showSearchTray, showResultsList]);


  // Prevent touchmove events in order to stop body from scrolling within Mobile Safari despite overflow:hidden. -Will
  const showSearchTrayRef = useRef(showSearchTray);
  useEffect(() => {
    showSearchTrayRef.current = showSearchTray;
  }, [showSearchTray]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0]?.clientY || 0;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!showSearchTrayRef.current) return;

      const resultsWrapperEl = resultsWrapperRef.current;
      const target = e.target;
      const isResultsTouch = Boolean(resultsWrapperEl && target instanceof Node && resultsWrapperEl.contains(target));

      if (!resultsWrapperEl || !isResultsTouch) {
        e.preventDefault();
        return;
      }

      const currentY = e.touches[0]?.clientY || 0;
      const deltaY = currentY - touchStartY.current;
      const isPullingDown = deltaY > 0;
      const isPushingUp = deltaY < 0;
      const isAtTop = resultsWrapperEl.scrollTop <= 0;
      const isAtBottom = resultsWrapperEl.scrollTop + resultsWrapperEl.clientHeight >= resultsWrapperEl.scrollHeight - 1;

      if ((isAtTop && isPullingDown) || (isAtBottom && isPushingUp)) {
        e.preventDefault();
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  return (
    <AnimatePresence
      onExitComplete={onClose}
    >
      {showSearchTray && (
        <>
          <motion.div
            className={cx(["search-tray", className])}
            initial={{ opacity: 0, filter: "blur(4px)" }}
            animate={{ opacity: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, filter: "blur(4px)", transition: { delay: 0 } }}
            transition={{ duration: 0.2, delay: 0.2, ease: easeOutQuad }}
            onAnimationStart={() => inputEl.current?.focus()}
          >
            <Container className={cx("content")}>
              <div className={cx("search-input-wrapper")}>
                <div className={cx(["search-input", { "focused": isFocused}])}>
                  <SearchIcon className={cx("search-input__icon")} />
                  <input
                    type="text"
                    name="search"
                    aria-label={searchAriaLabel || "Search"}
                    placeholder={searchPlaceholder || "Search"}
                    value={searchTerm}
                    onBlur={() => { setIsFocused(false) }}
                    onFocus={() => { setIsFocused(true) }}
                    onInput={handleInputChange}
                    onKeyDown={checkShouldClose}
                    ref={inputEl}
                    className={cx(["search-input__input", "text-m"])}
                    enterKeyHint="done"
                  />
                  <motion.button
                    initial={{ opacity: 0, pointerEvents: "none" }}
                    animate={{ opacity: searchTerm ? 1 : 0, pointerEvents: searchTerm ? "auto" : "none" }}
                    transition={{ duration: 0.2, ease: easeOutQuad }}
                    className={cx("search-input__clear")}
                    onClick={handleClearInput}
                    aria-label={clearAriaLabel || "Clear Search Input"}
                  >
                    <CloseIcon />
                  </motion.button>
                </div>
              </div>
              <div ref={resultsWrapperRef} className={cx("results-wrapper")} aria-live="polite" aria-label="Search results" role="listbox">
                <AnimatePresence mode="wait">
                  {showLoader && (
                    <MaterialLoader
                      key={'loader'}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, ease: easeOutQuad }}
                      className={cx("loader")}
                    />
                  )}
                  {showResultsList && (
                    <Results
                      key={'results'}
                      resultsCtaLabel={resultsCta}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, ease: easeOutQuad }}
                    />
                  )}
                  {showNoResults && (
                    <NoResults
                      copy={noResultsLabel || "No results found"}
                      key={'no-results'}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, ease: easeOutQuad }}
                    />
                  )}
                </AnimatePresence>
              </div>
            </Container>
          </motion.div>
          <motion.div
            className={cx("backdrop")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { delay: 0 } }}
            transition={{ duration: 0.2, delay: 0.2, ease: easeOutQuad }}
            onClick={closeTray}
          />
        </>
      )}
    </AnimatePresence>
  );
}
